import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {RouteName, RouteNameType} from '../../router/route-names';

const trace = getAppTracer('auth-resolve.helper');

export interface ResolveUserOptions {
    failRedirect?: RouteNameType;
    redirectParams?: Record<string, any>;
}

export interface ResolvedUserData {
    currentUser: any;
    accountData: any;
}

/**
 * Shared helper that fetches currentUser and accountData with unified error handling.
 * All failure paths redirect via navigateTo (throws redirect() in loader context).
 *
 * Failure scenarios:
 * 1. getCurrentUser() throws → redirect to failRedirect
 * 2. currentUser has no accountId → redirect to failRedirect
 * 3. getAccount() throws → redirect to failRedirect
 */
export async function resolveUserAndAccount(options?: ResolveUserOptions): Promise<ResolvedUserData> {
    const rootStore = getRootStore();
    const nav = rootStore.tsReactStateHelper;
    const redirectTo = options?.failRedirect ?? RouteName.LOGIN;
    const redirectParams = options?.redirectParams;

    let currentUser: any;

    try {
        currentUser = await rootStore.userService.getCurrentUser();
    } catch (err) {
        trace.error('getCurrentUser failed', {error: err});
        await nav.navigateTo(redirectTo, redirectParams);

        return undefined as never;
    }

    if (!currentUser) {
        trace.error('getCurrentUser returned empty');
        await nav.navigateTo(redirectTo, redirectParams);

        return undefined as never;
    }

    if (!currentUser.accountId) {
        trace.error('currentUser has no accountId');
        await nav.navigateTo(redirectTo, redirectParams);

        return undefined as never;
    }

    let accountData: any;

    try {
        accountData = await rootStore.accountService.getAccount(currentUser.accountId);
    } catch (err) {
        trace.error('getAccount failed', {error: err, accountId: currentUser.accountId});
        await nav.navigateTo(redirectTo, redirectParams);

        return undefined as never;
    }

    return {currentUser, accountData};
}
